import {
  App,
  FeatureToggles,
  getInactiveProductApps,
  isAppAllowedForJuristischePerson,
  isTiergruppeActive,
} from '@ps20-frontend/frontend-models';
import { Feature, Role } from '@ps20/bff-models';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { isAzam, isVertreter } from '@ps20-frontend/shop-integration-redux';
import { IAppState } from '../appState';
import content from '../../data/content-default.json';
import { isNeugeschaeftGesperrt } from './vorvertragsUebersicht.selector';
import {
  selectHasOwnedRowsWithoutTgvRow,
  selectIsTgvGroupIncomplete,
} from './tiergruppeWarenkorb.selector';
import { maxAngeboteImWarenkorb, maxAngeboteTarifiert } from './warenkorb.selector';

export const APP_ROLE_BLACKLIST = (state: IAppState): Partial<Record<App, Role[]>> => {
  if (selectFeatureToggle(state, Feature.UNFALL_VERTRIEBLICHE_TELEFONIE)) {
    return {
      [App.UNF]: [Role.UWPSHRK],
    };
  }
  return {
    [App.UNF]: [Role.VTTEL, Role.VTGEV, Role.VTKVM, Role.VTKUS, Role.UWPSHRK],
  };
};

export interface ProduktauswahlItem {
  appId: App;
  dashboardIcon: string;
  title: string;
  isSelected: boolean;
  checked: boolean;
}

export interface ProduktauswahlContentItem {
  id: App;
  title: string;
  icon: string;
  produktKdvId: string;
}

export interface SpecialProduktauswahlContentItem {
  id: string;
  title: string;
  icon: string;
  links: { [role: string]: string };
}

export const getProduktauswahlContent = (): Array<ProduktauswahlContentItem> => {
  return content.produkte as Array<ProduktauswahlContentItem>;
};

export const getProduktauswahlContentItem = (appId: App): ProduktauswahlContentItem => {
  return getProduktauswahlContent()
    .concat(getWeitereProdukte())
    .find(produktJson => produktJson.id === appId);
};

const mapContentItem = (state: IAppState) => {
  return (contentItem: ProduktauswahlContentItem) => {
    return {
      appId: contentItem.id,
      title: contentItem.title,
      dashboardIcon: contentItem.icon,
      checked: maxAngeboteTarifiert(contentItem.id, state),
      isSelected:
        (contentItem.id === App.TGV
          ? selectIsTgvGroupIncomplete(state) || selectHasOwnedRowsWithoutTgvRow(state)
          : maxAngeboteImWarenkorb(contentItem.id, state)) ||
        isNeugeschaeftGesperrt(contentItem.id, state),
    } as ProduktauswahlItem;
  };
};

export const selectProduktauswahlItems = (state: IAppState): Array<ProduktauswahlItem> => {
  return getProduktauswahlContent().map(mapContentItem(state));
};

export const selectAvailableProduktauswahlItems = (state: IAppState): Array<ProduktauswahlItem> => {
  return selectProduktauswahlItems(state)
    .filter(removeProdukteForJuristischePerson(state))
    .filter(isProductAllowedForRole(state));
};

export const selectAvailableProduktauswahlItemsForDashboard = (
  state: IAppState,
): Array<ProduktauswahlItem> => {
  const inactiveApps = getInactiveProductApps(
    state.transientState?.featureToggles as { [key: string]: boolean },
  );

  const availableItems = selectProduktauswahlItems(state)
    .filter(produkt => !inactiveApps.includes(produkt.appId))
    .filter(removeProdukteForJuristischePerson(state))
    .filter(isProductAllowedForRole(state));

  return removeAppsReplacedByTgv(availableItems, state.transientState?.featureToggles);
};

export const selectSelectableProdukte = (state: IAppState): Array<App> => {
  const produkte = selectProduktauswahlItems(state);
  const selectableProdukte = produkte.filter(produkt => !produkt.isSelected);
  return selectableProdukte.map(produkt => produkt.appId);
};

const getWeitereProdukte = (): Array<ProduktauswahlContentItem> => {
  return content.weitereProdukte as Array<ProduktauswahlContentItem>;
};

const removeProdukteForJuristischePerson = (state: IAppState) => {
  return (produkt: ProduktauswahlItem) =>
    state.juristisch
      ? isAppAllowedForJuristischePerson(produkt.appId) ||
        (produkt.appId === App.RS_VERMIETUNG &&
          ['24O', '99P', '99Q'].includes(state.versicherungsnehmer.businessForm?.wert))
      : true;
};

const isProductAllowedForRole =
  (state: IAppState) =>
  (produkt: ProduktauswahlItem): boolean => {
    return !(
      APP_ROLE_BLACKLIST(state)[produkt.appId] &&
      APP_ROLE_BLACKLIST(state)[produkt.appId].includes(state.transientState.role)
    );
  };

export const APPS_REPLACED_BY_TGV = new Set([App.TKV_V2, App.THH_HUND, App.THH_PFERD]);

export const removeAppsReplacedByTgv = (
  items: Array<ProduktauswahlItem>,
  featureToggles: FeatureToggles | null | undefined,
): Array<ProduktauswahlItem> => {
  if (!isTiergruppeActive(featureToggles) || !items.some(item => item.appId === App.TGV)) {
    return items;
  }
  return items.filter(item => !APPS_REPLACED_BY_TGV.has(item.appId));
};

export const selectAvailableWeitereProdukte = (state: IAppState): Array<ProduktauswahlItem> => {
  return getWeitereProdukte()
    .map(mapContentItem(state))
    .filter(removeProdukteForJuristischePerson(state));
};

export const selectAvailableSpecialWeitereProdukte =
  (): Array<SpecialProduktauswahlContentItem> => {
    return content.specialWeitereProdukte as SpecialProduktauswahlContentItem[];
  };

export const areWeitereProdukteEnabled = (state: IAppState): boolean => {
  return selectAvailableWeitereProdukte(state).length > 0;
};

export const isMaklerRole = (state: IAppState): boolean => isAzam(state.transientState.role);
export const isVertreterRole = (state: IAppState): boolean =>
  isVertreter(state.transientState.role);
